import { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { ChequePrintData } from '@/api/cheque.api';
import { ChequePrintSheet } from '@/components/banking/ChequePrintSheet';

/**
 * A cheque as it will come out of the printer, on screen (GAP-006, GAP-007).
 *
 * The print layer itself is invisible until the browser prints, which is correct for the paper and
 * useless for the person holding it: they press Print and find out what the layout does only after
 * a cheque has gone through the machine. This renders the very same <ChequePrintSheet>, scaled to
 * fit, on a sheet of simulated stationery — so the check happens before the paper is spent, not
 * after.
 *
 * Scaled with a CSS transform rather than by recomputing millimetres: the preview is then
 * provably the same element the printer receives, and cannot drift from it.
 */

const PX_PER_MM = 96 / 25.4;   // CSS reference pixels per millimetre

interface Props {
  data: ChequePrintData;
  /** Draw dashed outlines and labels over each positioned field. */
  showGuides?: boolean;
  /** Cap the rendered width. Defaults to the container's own width. */
  maxWidthPx?: number;
  className?: string;
}

export const ChequePreview = ({ data, showGuides = false, maxWidthPx, className }: Props) => {
  const hostRef  = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [overflow, setOverflow] = useState<string[]>([]);

  const widthPx  = data.pageWidthMm  * PX_PER_MM;
  const heightPx = data.pageHeightMm * PX_PER_MM;

  // Re-fit on container resize — a preview inside a dialog changes width when the window does,
  // and a cheque cropped at the right edge is worse than no preview at all.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || widthPx <= 0) return;

    const fit = () => {
      const available = Math.min(host.clientWidth || widthPx, maxWidthPx ?? Infinity);
      // Never scale up: a 210mm cheque blown past 1:1 just looks blurry.
      setScale(Math.min(1, available / widthPx));
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(host);
    return () => observer.disconnect();
  }, [widthPx, maxWidthPx]);

  // Which fields run off the paper.
  //
  // Measured from what actually rendered, not from the coordinates: a field's configured X can sit
  // comfortably inside the page and still overflow once the value is on it — a long payee name, an
  // amount in words, a stamp with no configured width at all. The sheet clips at the paper edge
  // exactly as a printer does, so without this the text simply vanishes and the layout looks fine.
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;

    const box   = sheet.getBoundingClientRect();
    const spans = [...sheet.querySelectorAll<HTMLElement>('span')];

    const label = (text: string) => {
      const t = text.trim();
      if (t === data.payeeName)      return 'payee name';
      if (t === data.amountInWords)  return 'amount in words';
      if (t === data.chequeDate)     return 'cheque date';
      if (t === data.chequeNumber)   return 'cheque number';
      if (t === data.crossingText)   return 'crossing';
      if (t === data.rubberStampText) return 'rubber stamp';
      return 'amount in figures';
    };

    const off = spans
      // A millimetre of tolerance: sub-pixel text metrics should not raise an alarm.
      .filter(el => el.getBoundingClientRect().right > box.right + PX_PER_MM)
      .map(el => label(el.innerText));

    setOverflow([...new Set(off)]);
  }, [data, scale]);

  return (
    <div ref={hostRef} className={className}>
      <div
        // The outer box takes the SCALED size so the layout around it reserves the right space —
        // a transform alone leaves the original footprint and tears a hole in the dialog.
        style={{
          width:  widthPx  * scale,
          height: heightPx * scale,
          maxWidth: '100%',
          overflow: 'hidden',
        }}
        className="relative rounded border border-slate-300 bg-white shadow-sm dark:border-slate-600"
      >
        <div style={{
          width:           widthPx,
          height:          heightPx,
          transform:       `scale(${scale})`,
          transformOrigin: 'top left',
          // The paper is white whatever the app's theme is — this is a picture of a physical
          // object, and inverting it in dark mode would be a picture of nothing that exists.
          background: '#ffffff',
          color:      '#111827',
          position:   'relative',
        }}>
          {showGuides && <Guides data={data} />}
          <div ref={sheetRef}><ChequePrintSheet data={data} /></div>
        </div>
      </div>

      {overflow.length > 0 && (
        <p className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Runs off the {data.pageWidthMm}mm page and will be cut off when printed:{' '}
            {overflow.join(', ')}. Adjust the position or width on the cheque layout.
          </span>
        </p>
      )}
    </div>
  );
};

/**
 * Dashed outlines showing where each field sits.
 *
 * For setting a layout up, not for reading a cheque: the coordinates are the thing being adjusted,
 * and a number in a form field says nothing about whether it lands on the payee line.
 */
const Guides = ({ data }: { data: ChequePrintData }) => {
  const boxes: { label: string; x: number; y: number; w?: number }[] = [
    { label: 'Payee',   x: data.payeeNameX,     y: data.payeeNameY,     w: data.payeeNameWidth },
    { label: 'Figures', x: data.amountFiguresX, y: data.amountFiguresY, w: data.amountFiguresWidth },
    { label: 'Words',   x: data.amountWordsX,   y: data.amountWordsY,   w: data.amountWordsWidth },
    { label: 'Date',    x: data.chequeDateX,    y: data.chequeDateY },
    { label: 'Cheque no.', x: data.chequeNoX,   y: data.chequeNoY },
  ];

  if (data.crossingTextX != null && data.crossingTextY != null)
    boxes.push({ label: 'Crossing', x: data.crossingTextX, y: data.crossingTextY });

  if (data.rubberStampX != null && data.rubberStampY != null)
    boxes.push({ label: 'Rubber stamp', x: data.rubberStampX, y: data.rubberStampY });

  return (
    <>
      {boxes.map(b => (
        <div key={b.label}
             style={{
               position: 'absolute',
               left:   `${b.x}mm`,
               top:    `${b.y}mm`,
               width:  b.w ? `${b.w}mm` : undefined,
               minWidth: b.w ? undefined : '18mm',
               height: '6mm',
               marginTop: '-1mm',
               border: '1px dashed rgba(37, 99, 235, 0.55)',
               background: 'rgba(37, 99, 235, 0.05)',
               pointerEvents: 'none',
             }}>
          <span style={{
            position: 'absolute',
            top: '-4mm',
            left: 0,
            fontSize: '2.6mm',
            lineHeight: 1,
            color: '#2563eb',
            whiteSpace: 'nowrap',
          }}>
            {b.label}
          </span>
        </div>
      ))}
    </>
  );
};
